import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { MonthYearPicker } from "../components/Filters";
import { MONTHS, formatRupiah } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2, Plus, Trash2, Wallet, ChevronDown, ChevronRight } from "lucide-react";

export default function TeachingFee() {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(8);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [dialog, setDialog] = useState(null); // teacher row
  const [form, setForm] = useState({ description: "", amount: "", date: "" });

  const load = () => {
    setLoading(true);
    api.get(`/teaching-fee?year=${year}&month=${month}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [year, month]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const addOther = async () => {
    const amt = parseInt(form.amount, 10);
    if (!form.description || isNaN(amt)) { toast.error("Isi keterangan & nominal"); return; }
    try {
      await api.post("/additional-earnings", {
        teacher_id: dialog.teacher_id, year, month, date: form.date || null,
        description: form.description, amount: amt,
      });
      toast.success("Item lain-lain ditambahkan");
      setForm({ description: "", amount: "", date: "" });
      load();
    } catch (e) { toast.error("Gagal"); }
  };

  const delOther = async (id) => {
    try { await api.delete(`/additional-earnings/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error("Gagal"); }
  };

  const currentRow = dialog && data?.rows.find((r) => r.teacher_id === dialog.teacher_id);

  return (
    <div className="space-y-5" data-testid="teaching-fee-page">
      <Toaster position="top-center" richColors />
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Teaching Fee</h2>
          <p className="text-sm text-muted-foreground">Perhitungan otomatis · {MONTHS[month - 1]} {year}</p>
        </div>
        <MonthYearPicker year={year} month={month} setYear={setYear} setMonth={setMonth} />
      </div>

      <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-5 bg-gradient-to-r from-gtsnavy to-gtsnavy-light text-white" data-testid="grand-total-card">
        <div className="flex items-center gap-3">
          <Wallet className="w-8 h-8" />
          <div>
            <p className="text-sm text-white/70">Total Gaji 6 Karyawan ({MONTHS[month - 1]} {year})</p>
            <p className="font-nunito font-extrabold text-3xl">{data ? formatRupiah(data.grand_total) : "-"}</p>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="p-16 flex justify-center"><Loader2 className="w-7 h-7 text-gtsorange animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {data?.rows.map((r) => {
            const open = expanded[r.teacher_id];
            return (
              <Card key={r.teacher_id} className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden" data-testid={`fee-row-${r.teacher_id}`}>
                <button onClick={() => toggle(r.teacher_id)} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-orange-50 transition-colors text-left">
                  {open ? <ChevronDown className="w-4 h-4 text-gtsorange" /> : <ChevronRight className="w-4 h-4 text-gtsorange" />}
                  <div className="flex-1">
                    <p className="font-semibold text-gtsnavy">{r.teacher_name}</p>
                    {r.is_staff && <Badge className="bg-gtsnavy/10 text-gtsnavy rounded-full text-[10px]">Staf</Badge>}
                  </div>
                  <p className="font-nunito font-extrabold text-gtsorange text-lg" data-testid={`fee-total-${r.teacher_id}`}>{formatRupiah(r.total)}</p>
                </button>
                {open && (
                  <div className="px-5 pb-5 space-y-3 border-t pt-4">
                    {r.schools.length > 0 && (
                      <div className="overflow-x-auto gts-scroll">
                        <table className="w-full text-sm">
                          <thead><tr className="text-muted-foreground text-xs">
                            <th className="text-left py-1">Sekolah</th><th className="text-right">Sesi</th><th className="text-right">Tarif</th><th className="text-right">Subtotal</th>
                          </tr></thead>
                          <tbody>
                            {r.schools.map((s) => (
                              <tr key={s.school_id} className="border-t">
                                <td className="py-1.5">{s.school_name}</td>
                                <td className="text-right">{s.sessions}</td>
                                <td className="text-right">{formatRupiah(s.rate)}</td>
                                <td className="text-right font-medium">{formatRupiah(s.subtotal)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {r.allowances.map((a, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gtsnavy">{a.label} <span className="text-xs text-muted-foreground">(tunjangan tetap)</span></span>
                        <span className="font-medium text-emerald-600">{formatRupiah(a.amount)}</span>
                      </div>
                    ))}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Lain-lain</p>
                        <Button size="sm" variant="outline" onClick={() => setDialog({ teacher_id: r.teacher_id, name: r.teacher_name })} className="rounded-full h-7 gap-1 text-xs" data-testid={`add-other-${r.teacher_id}`}>
                          <Plus className="w-3 h-3" /> Tambah
                        </Button>
                      </div>
                      {r.others.length === 0 && <p className="text-xs text-muted-foreground">Belum ada.</p>}
                      {r.others.map((o) => (
                        <div key={o.id} className="flex items-center justify-between text-sm">
                          <span>{o.description}{o.date ? ` · ${o.date}` : ""}</span>
                          <span className={`font-medium ${o.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                            {formatRupiah(o.amount)}
                            <button onClick={() => delOther(o.id)} className="ml-2 text-rose-400 hover:text-rose-600" data-testid={`del-other-${o.id}`}><Trash2 className="w-3 h-3 inline" /></button>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between border-t pt-2 font-nunito font-bold text-gtsnavy">
                      <span>Total Teaching Fee</span><span className="text-gtsorange">{formatRupiah(r.total)}</span>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Lain-lain · {dialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Keterangan</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" data-testid="other-desc" /></div>
            <div>
              <Label>Nominal (positif = tambahan, negatif = potongan)</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl mt-1" placeholder="cth: 50000 atau -25000" data-testid="other-amount" />
            </div>
            <div><Label>Tanggal (opsional)</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1" /></div>
            {currentRow?.others?.length > 0 && (
              <div className="text-xs text-muted-foreground">Total lain-lain saat ini: {formatRupiah(currentRow.other_total)}</div>
            )}
          </div>
          <DialogFooter><Button onClick={addOther} className="rounded-full bg-gtsorange text-white" data-testid="save-other-btn">Tambah</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
